import React, { useState, useMemo } from 'react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar, TrendingUp } from 'lucide-react';

const Dashboard = ({ records, numberLinks = [] }) => {
  const [dailyRange, setDailyRange] = useState('30'); // Default last 30 days

  const allRecords = useMemo(() => {
    return [
      ...records.map(r => ({ ...r, category: 'PUC' })),
      ...numberLinks.map(r => ({ ...r, category: 'Number Link' }))
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [records, numberLinks]);

  // Prepare data for monthly chart (All Time)
  const monthlyChartData = useMemo(() => {
    const monthlyDataMap = {};
    allRecords.forEach(record => {
      const dateObj = new Date(record.record_date);
      if (isNaN(dateObj)) return;
      
      const monthYear = dateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const sortTimestamp = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1).getTime();
      
      if (!monthlyDataMap[monthYear]) {
        monthlyDataMap[monthYear] = { name: monthYear, timestamp: sortTimestamp, collection: 0 };
      }
      monthlyDataMap[monthYear].collection += Number(record.amount || 0);
    });

    return Object.values(monthlyDataMap)
      .sort((a, b) => a.timestamp - b.timestamp)
      .map(({ name, collection }) => ({ name, collection }));
  }, [allRecords]);

  // Prepare data for daily chart based on selected range
  const dailyChartData = useMemo(() => {
    const dailyDataMap = {};
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - parseInt(dailyRange));
    const cutoffStr = cutoffDate.toLocaleDateString('en-CA');

    // Generate empty buckets for the selected range to ensure dates without collections show as 0
    for (let i = parseInt(dailyRange) - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const dayStr = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
      const sortTimestamp = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      dailyDataMap[dayStr] = { name: dayStr, timestamp: sortTimestamp, collection: 0 };
    }

    allRecords.forEach(record => {
      const recordDateStr = new Date(record.record_date).toLocaleDateString('en-CA');
      if (recordDateStr < cutoffStr) return; // Skip old records
      
      const dateObj = new Date(record.record_date);
      if (isNaN(dateObj)) return;
      
      const dayStr = dateObj.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
      
      if (dailyDataMap[dayStr]) {
        dailyDataMap[dayStr].collection += Number(record.amount || 0);
      }
    });

    return Object.values(dailyDataMap)
      .sort((a, b) => a.timestamp - b.timestamp)
      .map(({ name, collection }) => ({ name, collection }));
  }, [allRecords, dailyRange]);

  return (
    <div className="fade-in">
      {/* Monthly Chart (All Time) */}
      {monthlyChartData.length > 0 && (
        <div className="dash-card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className="metric-icon" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.2), rgba(139, 92, 246, 0.2))', color: 'var(--primary)' }}>
              <Calendar size={20} />
            </div>
            <h3 style={{ margin: 0 }}>Monthly Collection Trend (All Time)</h3>
          </div>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={monthlyChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(150, 150, 150, 0.2)" vertical={false} />
                <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `₹${val}`} />
                <Tooltip 
                  cursor={{ fill: 'rgba(150, 150, 150, 0.1)' }}
                  contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-main)' }}
                  itemStyle={{ color: 'var(--primary)', fontWeight: 'bold' }}
                  formatter={(value) => [`₹${value.toFixed(2)}`, 'Collection']}
                />
                <Bar dataKey="collection" fill="var(--primary)" radius={[4, 4, 0, 0]} maxBarSize={50} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Daily Chart */}
      <div className="dash-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className="metric-icon" style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))', color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
              <TrendingUp size={20} />
            </div>
            <h3 style={{ margin: 0 }}>Daily Collection Trend</h3>
          </div>
          <select 
            className="form-control" 
            style={{ width: 'auto', minWidth: '150px' }}
            value={dailyRange} 
            onChange={(e) => setDailyRange(e.target.value)}
          >
            <option value="7">Last 7 Days</option>
            <option value="15">Last 15 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 3 Months</option>
          </select>
        </div>
        <div style={{ width: '100%', height: 250 }}>
          <ResponsiveContainer>
            <AreaChart data={dailyChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorCollection" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(150, 150, 150, 0.2)" vertical={false} />
              <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `₹${val}`} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '8px', color: 'var(--text-main)' }}
                itemStyle={{ color: '#10b981', fontWeight: 'bold' }}
                formatter={(value) => [`₹${value.toFixed(2)}`, 'Collection']}
              />
              <Area type="monotone" dataKey="collection" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorCollection)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
